import React, { useEffect, useState } from 'react';
import { getInTransitList, getInTransitDetail } from '../api/inTransit';

// ── helpers ───────────────────────────────────────────────────────────────

function formatDispatchedAt(raw) {
  if (!raw) return '—';
  // Backend returns "YYYY-MM-DD HH:MM:SS.ffffff" (UTC naive). Keep it readable.
  const d = new Date(raw.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function formatScheduled(date, time) {
  if (!date) return '—';
  const d = new Date(date + 'T00:00:00');
  const dateStr = isNaN(d.getTime())
    ? date
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  return time ? `${dateStr} · ${time}` : dateStr;
}

function summariseChildren(children) {
  if (!children || children.length === 0) return '—';
  return children
    .map(c => {
      const g = c.gender ? c.gender.charAt(0).toUpperCase() + c.gender.slice(1) : '?';
      return `${g} ${c.size || ''}`.trim();
    })
    .join(' · ');
}

function ChildrenList({ items }) {
  if (!items || items.length === 0) {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((c, i) => {
        const g = c.gender ? c.gender.charAt(0).toUpperCase() + c.gender.slice(1) : '?';
        return (
          <span
            key={i}
            className="bg-gray-100 text-gray-700 text-xs font-medium px-2 py-1 rounded"
          >
            {g} · {c.size}
          </span>
        );
      })}
    </div>
  );
}

function ItemStatusBadge({ item }) {
  if (item.is_sold) {
    const sold = item.sold_price != null ? ` ₹${item.sold_price}` : '';
    return (
      <span className="bg-zoddle-green text-white text-xs font-semibold px-2 py-1 rounded">
        Sold{sold}
      </span>
    );
  }
  if (item.current_status === 'in_transit') {
    return (
      <span className="bg-zoddle-blue text-white text-xs font-semibold px-2 py-1 rounded">
        In box
      </span>
    );
  }
  if (item.current_status === 'missing') {
    return (
      <span className="bg-zoddle-pink text-white text-xs font-semibold px-2 py-1 rounded">
        Missing
      </span>
    );
  }
  return (
    <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2 py-1 rounded">
      {item.current_status}
    </span>
  );
}

// ── Detail modal ──────────────────────────────────────────────────────────

function DetailModal({ appointmentId, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getInTransitDetail(appointmentId)
      .then(d => { if (alive) { setData(d); setError(null); } })
      .catch(err => {
        if (alive) setError(err.response?.data?.detail || err.message || 'Failed to load detail');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [appointmentId]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-gray-800">Pack in transit</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-zoddle-pink text-2xl leading-none"
            aria-label="Close"
          >×</button>
        </div>

        {loading && <div className="p-8 text-center text-gray-500">Loading…</div>}
        {error && <div className="p-8 text-center text-zoddle-pink">{error}</div>}

        {data && (
          <div className="p-6 space-y-6">
            {/* Customer + Dispatch blocks side-by-side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded p-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Customer
                </h3>
                <div className="text-base font-semibold text-gray-800">
                  {data.customer.full_name || '—'}
                </div>
                <div className="text-sm text-gray-600">{data.customer.phone || '—'}</div>
                <div className="text-sm text-gray-600 mt-2">
                  {data.customer.address_line_1 || ''}
                  {data.customer.address_line_2 ? `, ${data.customer.address_line_2}` : ''}
                </div>
                <div className="text-sm text-gray-600">
                  {data.customer.area}{data.customer.pincode ? ` · ${data.customer.pincode}` : ''}
                </div>
              </div>

              <div className="bg-gray-50 rounded p-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Dispatch
                </h3>
                <div className="text-sm text-gray-700">
                  <span className="text-gray-500">Scheduled: </span>
                  {formatScheduled(data.appointment.scheduled_date, data.appointment.scheduled_time)}
                </div>
                <div className="text-sm text-gray-700">
                  <span className="text-gray-500">Dispatched: </span>
                  {formatDispatchedAt(data.appointment.dispatched_at)}
                </div>
                <div className="text-sm text-gray-700">
                  <span className="text-gray-500">Delivery: </span>
                  {data.delivery_person_name || <span className="text-gray-400">not assigned</span>}
                </div>
                <div className="text-sm text-gray-700 mt-2">
                  <ChildrenList items={data.children} />
                </div>
              </div>
            </div>

            {/* Items table */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Items in box ({data.items.length})
              </h3>
              <div className="overflow-x-auto border border-gray-200 rounded">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Barcode</th>
                      <th className="text-left px-3 py-2 font-semibold">Design</th>
                      <th className="text-left px-3 py-2 font-semibold">Brand</th>
                      <th className="text-left px-3 py-2 font-semibold">Size</th>
                      <th className="text-right px-3 py-2 font-semibold">Price</th>
                      <th className="text-left px-3 py-2 font-semibold">Condition</th>
                      <th className="text-left px-3 py-2 font-semibold">Mini-pack</th>
                      <th className="text-left px-3 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-3 py-6 text-center text-gray-400">
                          No items recorded for this pack.
                        </td>
                      </tr>
                    )}
                    {data.items.map(item => (
                      <tr
                        key={item.item_id}
                        className={`border-t border-gray-100 ${item.is_sold ? 'bg-green-50' : ''}`}
                      >
                        <td className="px-3 py-2 font-mono text-xs text-gray-700">
                          {item.zoddle_barcode}
                        </td>
                        <td className="px-3 py-2 text-gray-800">{item.design_name || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">{item.brand_name || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">{item.size || '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-700">
                          {item.zoddle_price != null ? `₹${item.zoddle_price}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{item.condition || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{item.mini_pack_code || '—'}</td>
                        <td className="px-3 py-2">
                          <ItemStatusBadge item={item} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────

export default function InTransit() {
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  function load() {
    setLoading(true);
    getInTransitList()
      .then(r => { setRows(r); setError(null); })
      .catch(err => setError(err.response?.data?.detail || err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800">In Transit</h2>
        <button
          onClick={load}
          className="text-sm text-gray-500 hover:text-zoddle-green transition-colors"
        >
          Refresh
        </button>
      </div>

      {loading && <div className="text-gray-500">Loading…</div>}
      {error && <div className="text-zoddle-pink">{error}</div>}

      {!loading && !error && rows.length === 0 && (
        <div className="text-gray-500 italic py-8 text-center bg-white border border-gray-200 rounded">
          No packs currently in transit.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="overflow-x-auto bg-white border border-gray-200 rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Customer</th>
                <th className="text-left px-4 py-3 font-semibold">Area</th>
                <th className="text-left px-4 py-3 font-semibold">Scheduled</th>
                <th className="text-left px-4 py-3 font-semibold">Dispatched</th>
                <th className="text-left px-4 py-3 font-semibold">Delivery</th>
                <th className="text-left px-4 py-3 font-semibold">Children</th>
                <th className="text-right px-4 py-3 font-semibold">Items</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr
                  key={row.appointment_id}
                  onClick={() => setSelectedId(row.appointment_id)}
                  className="border-t border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-800">{row.customer_name || '—'}</div>
                    <div className="text-xs text-gray-500">{row.phone || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <div>{row.area || '—'}</div>
                    <div className="text-xs text-gray-500">{row.pincode || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {formatScheduled(row.scheduled_date, row.scheduled_time)}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {formatDispatchedAt(row.dispatched_at)}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {row.delivery_person_name || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {summariseChildren(row.children)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 font-medium">
                    {row.item_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && (
        <DetailModal
          appointmentId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
